import React, { useEffect, useMemo, useState } from 'react';
import {
  Edit, NumberInput, ShowButton, SimpleForm, TextInput, TopToolbar, maxLength, minLength, required,
  useDataProvider, useGetOne, useRecordContext,
} from 'react-admin';
import { Alert, Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import CheckChainUsesModule, { ModuleKey } from '../../components/CheckChainUsesModule';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { GiftProductType, GiftRuleType } from '../../models';
import { ROLETA_RESOURCE, RoletaSegment, RoletaStatus } from '../../models/roleta';
import RoletaImageInput from './RoletaImageInput';
import RoletaStatusChip from './RoletaStatusChip';
import SegmentEditor, { PrizeProductOption, validateSegments } from './components/SegmentEditor';
import RoletaPreview from './components/RoletaPreview';
import SimulationPanel from './components/SimulationPanel';

const Section: React.FC<{ title: string; children: React.ReactNode; hint?: string }> = ({ title, children, hint }) => (
  <Card sx={{ mb: 2, width: '100%' }}>
    <CardContent>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: hint ? 0 : 2 }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {hint}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

const Title = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Typography variant="h6" component="span">Roleta: {record.name}</Typography>
      <RoletaStatusChip status={record.status} />
    </Box>
  );
};

const EditActions = () => (
  <TopToolbar>
    <ShowButton />
  </TopToolbar>
);

const productLabel = (product: any): string =>
  product?.productName
  || product?.name
  || GiftProductType.translateType(product?.productType as GiftProductType)
  || 'Prêmio';

/**
 * Produtos do brinde de prêmios da roleta.
 *
 * Preferimos o que o detalhe da roleta já traz (`prizeGift`); quando não vier,
 * procuramos o brinde cuja regra "Prêmio da Roleta" aponta para esta roleta.
 */
const usePrizeProducts = (roletaId?: string, record?: any) => {
  const dataProvider = useDataProvider();
  const chainId = getChainId();
  const [products, setProducts] = useState<PrizeProductOption[]>([]);
  const [loading, setLoading] = useState(true);

  const embedded: any[] | undefined = record?.prizeGift?.giftProducts;
  const embeddedKey = embedded ? embedded.map((product: any) => product.id).join(',') : '';

  useEffect(() => {
    if (!roletaId) return;
    let active = true;

    if (embedded?.length) {
      setProducts(embedded.map((product: any) => ({ id: product.id, label: productLabel(product) })));
      setLoading(false);
      return;
    }

    setLoading(true);
    dataProvider.getList('gifts', {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      filter: { chainId, ruleTypes: [GiftRuleType.roleta_prize] },
    })
      .then(({ data }) => {
        if (!active) return;
        const gift = (data as any[]).find((candidate) => (candidate.giftRules || []).some(
          (rule: any) => rule?.type === GiftRuleType.roleta_prize && rule?.roletaId === roletaId,
        ));
        const giftProducts: any[] = gift?.giftProducts || [];
        setProducts(giftProducts.map((product) => ({ id: product.id, label: productLabel(product) })));
      })
      .catch(() => { if (active) setProducts([]); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roletaId, embeddedKey, chainId]);

  return { products, loading };
};

const EditBody = () => {
  const record = useRecordContext();
  const chainId = getChainId();
  const { data: chain } = useGetOne('chains', { id: chainId }, { enabled: !!chainId });

  const [segments, setSegments] = useState<RoletaSegment[]>([]);
  const [dirty, setDirty] = useState(false);

  const recordSegments: RoletaSegment[] | undefined = record?.segments;
  const recordSegmentsKey = recordSegments ? JSON.stringify(recordSegments) : '';

  useEffect(() => {
    if (!recordSegments) return;
    setSegments(recordSegments.map((segment, index) => ({ ...segment, segmentIndex: segment.segmentIndex ?? index })));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordSegmentsKey]);

  const { products: prizeProducts, loading: loadingPrizeProducts } = usePrizeProducts(record?.id as string, record);

  const status: RoletaStatus = (record?.status as RoletaStatus) ?? RoletaStatus.Draft;
  const isDraft = status === RoletaStatus.Draft;
  const isClosed = status === RoletaStatus.Closed;
  const baseColor = chain?.appColor || '';

  const { errors: segmentErrors } = useMemo(() => validateSegments(segments), [segments]);

  if (!record) return null;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 7 }}>
        {isClosed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Roleta encerrada. O cadastro fica só para consulta e histórico.
          </Alert>
        )}

        <SimpleForm sx={{ p: 0 }}>
          <Section title="Identificação">
            <TextInput
              source="name"
              label="Nome da roleta"
              validate={[required(), minLength(3), maxLength(120)]}
              disabled={isClosed}
              fullWidth
              helperText={false}
            />
            <TextInput
              source="description"
              label="Descrição"
              multiline
              rows={3}
              disabled={isClosed}
              fullWidth
              helperText={false}
            />
            <RoletaImageInput
              source="imageUrl"
              label="Banner"
              kind="banner"
              helperText="Faixa larga no topo da tela da roleta. JPG/PNG/WebP até 5MB."
            />
            <RoletaImageInput
              source="centerImageUrl"
              label="Arte do centro da roda"
              kind="center"
              helperText="Fica no miolo da roda, que não gira. Vazio = logo da rede. Use para dar cara de campanha (Natal, Copa)."
            />
            <TextInput
              source="regulationUrl"
              label="Link do regulamento"
              disabled={isClosed}
              fullWidth
              helperText="Endereço da página com as regras da campanha"
            />
          </Section>

          <Section
            title="Período"
            hint={isDraft ? undefined : 'A data de início não muda depois que a roleta é ativada.'}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
              <DateTimeInputUTC
                source="startAt"
                label="Início"
                disabled={!isDraft}
                sx={{ flex: 1 }}
                helperText={false}
              />
              <DateTimeInputUTC
                source="endAt"
                label="Fim"
                disabled={isClosed}
                sx={{ flex: 1 }}
                helperText="Pode ser esticada com a roleta no ar"
              />
            </Stack>
          </Section>

          <Section title="Regras do giro">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
              <NumberInput
                source="spinValidityDays"
                label="Validade do giro (dias)"
                min={1}
                disabled={isClosed}
                sx={{ flex: 1 }}
                helperText="Vazio = o giro vale até o fim da roleta"
              />
              <NumberInput
                source="maxExtraSpinsPerTicket"
                label='Máximo de "gire de novo" seguidos'
                min={0}
                disabled={isClosed}
                sx={{ flex: 1 }}
                helperText="Evita corrente infinita de giros"
              />
            </Stack>
          </Section>
        </SimpleForm>

        <Box sx={{ mt: 1 }}>
          <SegmentEditor
            roletaId={String(record.id)}
            roletaStatus={status}
            segments={segments}
            onChange={(next) => { setSegments(next); setDirty(true); }}
            prizeProducts={prizeProducts}
            loadingPrizeProducts={loadingPrizeProducts}
            baseColor={baseColor}
            onSaved={() => setDirty(false)}
          />
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 5 }}>
        <Box sx={{ position: 'sticky', top: 88 }}>
          <RoletaPreview
            segments={segments}
            baseColor={baseColor}
            roletaName={record.name as string}
            imageUrl={record.imageUrl as string}
            centerImageUrl={record.centerImageUrl as string}
          />
          <SimulationPanel
            roletaId={String(record.id)}
            segments={segments}
            disabled={dirty || segmentErrors.length > 0}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

const RoletaEdit: React.FC = (props: any) => (
  <CheckChainUsesModule module={ModuleKey.ROLETA}>
    <Permission permission={PERMISSIONS.GIFT}>
      <Edit
        {...props}
        resource={ROLETA_RESOURCE}
        title={<Title />}
        actions={<EditActions />}
        mutationMode="pessimistic"
      >
        <EditBody />
      </Edit>
    </Permission>
  </CheckChainUsesModule>
);

export default RoletaEdit;
